// Minimal typed API client shared by web + mobile.
export interface ApiClientOptions {
  baseUrl: string;
  getToken?: () => string | null | Promise<string | null>;
  onUnauthorized?: () => void;
}

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

export function createApiClient(opts: ApiClientOptions) {
  async function request<T>(
    method: string,
    path: string,
    body?: unknown,
    init?: RequestInit,
  ): Promise<T> {
    const token = opts.getToken ? await opts.getToken() : null;
    const headers: Record<string, string> = {
      "content-type": "application/json",
      ...(init?.headers as Record<string, string> | undefined),
    };
    if (token) headers.authorization = `Bearer ${token}`;

    // `credentials` is honored by browser/RN fetch. The `as RequestInit` assertion
    // skips the excess-property check when this module is type-checked under
    // @cloudflare/workers-types (whose RequestInit lacks `credentials`).
    const res = await fetch(`${opts.baseUrl}${path}`, {
      method,
      headers,
      credentials: "include",
      body: body !== undefined ? JSON.stringify(body) : undefined,
      ...init,
    } as RequestInit);

    if (res.status === 401) opts.onUnauthorized?.();

    const text = await res.text();
    const data = text ? safeJson(text) : null;
    if (!res.ok) {
      const msg =
        (data && typeof data === "object" && "error" in data
          ? String((data as Record<string, unknown>).error)
          : res.statusText) || "Request failed";
      throw new ApiError(res.status, msg, data);
    }
    return data as T;
  }

  return {
    get: <T>(p: string) => request<T>("GET", p),
    post: <T>(p: string, b?: unknown) => request<T>("POST", p, b),
    patch: <T>(p: string, b?: unknown) => request<T>("PATCH", p, b),
    del: <T>(p: string) => request<T>("DELETE", p),
    raw: request,
  };
}

function safeJson(t: string): unknown {
  try {
    return JSON.parse(t);
  } catch {
    return t;
  }
}

export type ApiClient = ReturnType<typeof createApiClient>;
